// Query-scoping helpers for dimensions whose values only exist under a
// specific report state.

// Closure type (status.details.closed) is only ever set on closed reports, so a
// breakdown by it must be scoped to status.current == closed. Otherwise every
// open report — which simply has no closure type — falls into the "no type"
// ("—") bucket and inflates it, mixing "open" with "closed, type not assigned".
// Mirrors the BI breakdown (casos.py _tipos_cierre), which AND-s this first.
export const CLOSED_STATUS_CONDITION = {
  field: "status__current",
  operator: "eq",
  value: "closed",
};

/**
 * AND-s the closed-status condition into an EQL filter tree when needed.
 * Idempotent: won't duplicate an existing status.current == closed condition.
 *
 * @param {Object|undefined} filters - EQL filter tree ({ AND: [...] }) or undefined
 * @param {boolean} needsClosed - true when a closure-type dimension is in play
 * @returns {Object|undefined} the (possibly) scoped filter tree
 */
export const scopeToClosed = (filters, needsClosed) => {
  if (!needsClosed) return filters;
  const conditions = filters?.AND ? [...filters.AND] : filters ? [filters] : [];
  const already = conditions.some(
    (c) => c?.field === "status__current" && c?.value === "closed"
  );
  if (!already) conditions.push(CLOSED_STATUS_CONDITION);
  return { AND: conditions };
};

// Assigned user. Assignment is not a report column: each Assignment row holds a
// user and an `assigned_reports` array of report IDs, and EQL `filters` can only
// address Report fields. So the filter tree carries a front-only marker
// ({ field: ASSIGNED_USER_FIELD, value: userId }) that fetchPivotData resolves,
// right before querying, into `id in <that user's assigned_reports>`.
//
// Resolved at query time on purpose, never baked in at Apply: saved views and
// dashboard pieces persist appliedFilters, and a frozen ID list would ignore
// reports assigned (or unassigned) after the save.
export const ASSIGNED_USER_FIELD = "assigned_user";

const conditionsOf = (filters) =>
  filters?.AND ? filters.AND : filters ? [filters] : [];

/** The user id the filter tree narrows by assignment, or null. */
export const assignedUserOf = (filters) => {
  const marker = conditionsOf(filters).find(
    (c) => c?.field === ASSIGNED_USER_FIELD
  );
  return marker?.value ?? null;
};

/**
 * Swaps the assigned-user marker for `id in reportIds`, leaving every other
 * condition as is: the same query, minus the reports the user is not assigned
 * to. An empty list matches nothing, which is correct — a user with no
 * assignments has no reports to show. Returns the input untouched when there is
 * no marker.
 */
export const restrictToReportIds = (filters, reportIds) => {
  const conditions = conditionsOf(filters);
  if (!conditions.some((c) => c?.field === ASSIGNED_USER_FIELD)) return filters;
  return {
    AND: [
      ...conditions.filter((c) => c?.field !== ASSIGNED_USER_FIELD),
      { field: "id", operator: "in", value: [...new Set(reportIds || [])] },
    ],
  };
};
